/**
 * Shopify cloud storage for user_design metaobjects (App Proxy).
 */
(function (global) {
  const Core = () => global.BraceletCore;

  function apiBase() {
    const cfg = Core()?.CONFIG?.() || global.BraceletDesignerConfig || {};
    const base = (cfg.designApiBaseUrl || '').trim().replace(/\/$/, '');
    return base;
  }

  function enabled() {
    return !!apiBase();
  }

  function requireLogin() {
    const CoreApi = Core();
    if (!CoreApi) return false;
    return CoreApi.isLoggedInCustomer();
  }

  async function request(path, options = {}) {
    const base = apiBase();
    if (!base) throw new Error('Design cloud API is not configured');

    const root = base.replace(/\/+$/, '');
    const suffix =
      path == null || path === '' || path === '/'
        ? ''
        : path.startsWith('/')
          ? path
          : `/${path}`;
    const url = `${root}${suffix}`;
    const headers = {
      Accept: 'application/json',
      ...(options.headers || {}),
    };
    if (options.body && !headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }

    const cfg = Core()?.CONFIG?.() || {};
    if (cfg.customerId && !headers['X-Customer-Id']) {
      headers['X-Customer-Id'] = String(cfg.customerId);
    }

    const res = await fetch(url, {
      method: options.method || 'GET',
      credentials: 'same-origin',
      headers,
      body: options.body ? JSON.stringify(options.body) : undefined,
    });

    const text = await res.text();
    let data;
    try {
      data = text ? JSON.parse(text) : {};
    } catch {
      throw new Error(text || `HTTP ${res.status}`);
    }

    if (!res.ok) {
      const err = new Error(data.error || `HTTP ${res.status}`);
      err.status = res.status;
      err.cloudUnavailable = res.status === 404 || res.status === 502 || res.status === 503;
      throw err;
    }
    return data;
  }

  function isCloudUnavailableError(err) {
    if (!err) return false;
    if (err.cloudUnavailable) return true;
    const status = err.status;
    if (status === 404 || status === 502 || status === 503 || status === 401) return true;
    const msg = String(err.message || '');
    return /404|not found|failed to fetch|network|load failed/i.test(msg);
  }

  function isTruthyPublished(value) {
    return value === true || value === 1 || value === '1';
  }

  function normalizeDesign(raw) {
    if (!raw) return null;
    let designJson = raw.designJson;
    if (typeof designJson === 'string') {
      try {
        designJson = JSON.parse(designJson);
      } catch {
        designJson = null;
      }
    }
    return {
      id: raw.handle || raw.id,
      handle: raw.handle || raw.id,
      title: raw.title || 'My Crystal Bracelet',
      designJson,
      wristSizeCm: raw.wristSizeCm ?? 15,
      beadCount: raw.beadCount ?? 0,
      price: raw.price ?? 0,
      published: isTruthyPublished(raw.published),
      sort: raw.sort ?? 0,
      viewCount: Number(raw.viewCount ?? raw.sort) || 0,
      customerId: raw.customerId,
      createdAt: raw.createdAt,
      updatedAt: raw.updatedAt,
      kind:
        (raw.beadCount ?? designJson?.b?.length ?? 0) <= 1 ? 'single' : 'bracelet',
    };
  }

  async function listMine() {
    const data = await request('?list=mine');
    return (data.designs || []).map(normalizeDesign).filter((d) => d?.designJson);
  }

  async function listPlaza(options = {}) {
    const page = Math.max(1, Number(options.page) || 1);
    const limit = Math.min(100, Math.max(1, Number(options.limit) || 8));
    const params = new URLSearchParams({
      list: 'plaza',
      page: String(page),
      limit: String(limit),
    });
    if (options.sort) params.set('sort', String(options.sort));
    if (options.wristMin != null && options.wristMin !== '') {
      params.set('wrist_min', String(options.wristMin));
    }
    if (options.wristMax != null && options.wristMax !== '') {
      params.set('wrist_max', String(options.wristMax));
    }
    if (options.priceMin != null && options.priceMin !== '') {
      params.set('price_min', String(options.priceMin));
    }
    if (options.priceMax != null && options.priceMax !== '') {
      params.set('price_max', String(options.priceMax));
    }
    const data = await request(`?${params.toString()}`);
    let designs = (data.designs || []).map(normalizeDesign).filter((d) => d?.designJson);

    if (data.pagination) {
      return { designs, pagination: data.pagination };
    }

    const total = designs.length;
    if (total > limit) {
      const start = (page - 1) * limit;
      designs = designs.slice(start, start + limit);
    }
    const totalPages = total ? Math.ceil(total / limit) : 0;
    return {
      designs,
      pagination: { page, limit, total, totalPages },
    };
  }

  async function getByHandle(handle) {
    const data = await request(`?handle=${encodeURIComponent(handle)}`);
    return normalizeDesign(data.design);
  }

  async function save({ handle, title, designJson, wristSizeCm, beadCount, price }) {
    if (!requireLogin()) throw new Error('LOGIN_REQUIRED');
    const data = await request('', {
      method: 'POST',
      body: {
        handle: handle || undefined,
        title,
        designJson,
        wristSizeCm,
        beadCount,
        price,
      },
    });
    return normalizeDesign(data.design);
  }

  async function remove(handle) {
    if (!requireLogin()) throw new Error('LOGIN_REQUIRED');
    const h = String(handle || '').trim();
    if (!h) throw new Error('handle required');
    // n8n 多 Method Webhook 常丢失 DELETE，优先 POST + action=delete
    try {
      await request('', {
        method: 'POST',
        body: { action: 'delete', handle: h },
      });
      return true;
    } catch (postErr) {
      try {
        await request(`?handle=${encodeURIComponent(h)}`, { method: 'DELETE' });
        return true;
      } catch {
        throw postErr;
      }
    }
  }

  async function setPublished(handle, published, sort) {
    if (!requireLogin()) throw new Error('LOGIN_REQUIRED');
    const body = { handle, published };
    if (sort != null) body.sort = sort;
    const data = await request('', {
      method: 'POST',
      body,
    });
    return normalizeDesign(data.design);
  }

  async function createShareCode({ title, designJson, wristSizeCm, beadCount, price }) {
    const data = await request('', {
      method: 'POST',
      body: {
        share: true,
        title,
        designJson,
        wristSizeCm,
        beadCount,
        price,
      },
    });
    const digits = String(data.code || data.displayCode || '')
      .replace(/^CHDIY/i, '')
      .replace(/^Design/i, '')
      .replace(/\D/g, '')
      .slice(0, 8);
    const displayCode =
      global.BraceletCore?.formatShareDisplayCode?.(digits) ||
      `CHDIY${digits.padStart(8, '0')}`;
    return { code: digits, displayCode };
  }

  async function resolveShareCode(code) {
    const digits =
      global.BraceletCore?.parseShortShareCode?.(code) ||
      String(code || '')
        .replace(/^CHDIY/i, '')
        .replace(/^Design/i, '')
        .replace(/\D/g, '')
        .slice(0, 8);
    if (!digits) throw new Error('Invalid share code');
    const data = await request(`?share=${encodeURIComponent(digits)}`);
    const raw = data.design || data;
    let designJson = raw.designJson ?? raw.design_json;
    if (typeof designJson === 'string') {
      try {
        designJson = JSON.parse(designJson);
      } catch {
        designJson = null;
      }
    }
    return {
      title: raw.title || 'My Crystal Bracelet',
      designJson,
    };
  }

  global.DesignCloudApi = {
    enabled,
    requireLogin,
    isCloudUnavailableError,
    listMine,
    listPlaza,
    getByHandle,
    save,
    remove,
    setPublished,
    createShareCode,
    resolveShareCode,
    normalizeDesign,
  };
})(typeof window !== 'undefined' ? window : globalThis);
